'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import {
  useGetCompanyManagersQuery,
  useGetCompanyByIdQuery,
  useGetCompanyUserQuery,
  useGetManagerReportsQuery,
} from 'src/api/redux';
import {
  Card,
  CardContent,
  TabSet,
  Tabs,
  Tab,
  TabPanel,
  CircularProgress,
} from 'src/components';
import ManagersTabContent from './components/ManagersTabContent';
import ReportsTabContent from './components/ReportsTabContent';

const ManagerCard = () => {
  // Get IDs from route params
  const { companyId, userId } = useParams<{ companyId: string; userId: string }>();

  // Get data from Redux queries
  const { data: currentCompany } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );

  const { data: currentUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );

  const { isLoading: isManagersLoading } = useGetCompanyManagersQuery(
    { companyId },
    { skip: !companyId }
  );

  const isManager = currentUser?.permissions?.is_manager;

  // Fetch manager reports to initialize selection state
  const { data: reports } = useGetManagerReportsQuery(
    { companyId, managerId: userId, pageSize: '1000' },
    { skip: !companyId || !userId || !isManager }
  );

  // Get the initial list of manager reports
  const managerReports = useMemo(() => {
    if (!reports?.results) return [];
    return reports?.results?.map((report: { id: number }) => report.id);
  }, [reports]);

  // Lifted state for ReportsTabContent - survives tab switches
  const [selectedReports, setSelectedReports] = useState<Set<number>>(
    new Set()
  );
  const [isReportsDirty, setIsReportsDirty] = useState(false);

  // Sync selectedReports with managerReports when form is clean
  // This handles: initial load, after successful save, and data refetch
  useEffect(() => {
    if (!isReportsDirty) {
      setSelectedReports(new Set(managerReports));
    }
  }, [managerReports, isReportsDirty]);

  // Reset reports state to initial values (used by cancel button)
  const resetReportsState = () => {
    setSelectedReports(new Set(managerReports));
    setIsReportsDirty(false);
  };

  const loading = isManagersLoading || !currentCompany;

  if (!currentCompany) {
    return null;
  }

  return (
    <Card className="relative">
      <CardContent className="p-6">
        <div className="flex flex-col gap-6">
          <Tabs>
            <div className="flex flex-col w-full">
              <TabSet id="manager-tabs">
                <Tab tabSetId="manager-tabs" index={0}>
                  Managers
                </Tab>
                {isManager && (
                  <Tab tabSetId="manager-tabs" index={1}>
                    Direct Reports
                  </Tab>
                )}
              </TabSet>

              <TabPanel tabSetId="manager-tabs" index={0}>
                <ManagersTabContent />
              </TabPanel>
              <TabPanel tabSetId="manager-tabs" index={1}>
                <ReportsTabContent
                  selectedReports={selectedReports}
                  setSelectedReports={setSelectedReports}
                  isDirty={isReportsDirty}
                  setIsDirty={setIsReportsDirty}
                  managerReports={managerReports}
                  resetState={resetReportsState}
                />
              </TabPanel>
            </div>
          </Tabs>
        </div>
      </CardContent>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
    </Card>
  );
};

export default ManagerCard;
